import moment from 'moment-timezone';
import {useCallback, useEffect} from 'react';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import * as utils from '~/common/utils';
import {AddMultipleDates} from '~/SignUpSheets/Create/routes/Customize/components/AdvancedOptions/modal/steps/AddMultipleDates';
import {useUpdateSheet} from '~/SignUpSheets/hooks/useUpdateSheet';
import {selectBlueprintType, selectFieldSchema} from '~/SignUpSheets/reducers/blueprint/selectors';
import {
  SCOPE_HOST,
  UPDATE_MULTIPLE_DATES_MODAL,
  UPDATE_MULTIPLE_DATES_MODAL_REPEATING_TAB_MONTHLY_SPECIFIER,
} from '~/SignUpSheets/reducers/sheet/constants';
import {initialMultipleDatesModalState} from '~/SignUpSheets/reducers/sheet/reducer';
import {
  selectMultipleDatesModalDates,
  selectMultipleDatesModalEndDate,
  selectMultipleDatesModalStartDate,
  selectMultipleDatesModalTab,
} from '~/SignUpSheets/reducers/sheet/selectors';
import {schemaToJson} from '~/SignUpSheets/utils/schema';
import {Button} from '~library/atoms/Button';
import {Divider} from '~library/atoms/Divider';

export const OverlayContent = ({onClose, trackChangeUpstream}) => {
  const dispatch = useDispatch();
  const tab = useSelector(selectMultipleDatesModalTab);
  const startDate = useSelector(selectMultipleDatesModalStartDate);
  const endDate = useSelector(selectMultipleDatesModalEndDate);
  const dates = useSelector(selectMultipleDatesModalDates, shallowEqual);
  const fieldSchema = useSelector(selectFieldSchema, shallowEqual);
  const blueprintType = useSelector(selectBlueprintType);
  const {updateSheetDetails} = useUpdateSheet();

  const okButtonDisabled = (tab === 'repeating' && endDate === null) || dates?.length === 0;

  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  const onClickOk = useCallback(() => {
    const newSignupOptionsList = dates.map((date) => {
      const dateSchemaJson = schemaToJson(fieldSchema, blueprintType, SCOPE_HOST);
      dateSchemaJson.date = date;
      return dateSchemaJson;
    });

    trackChangeUpstream?.(newSignupOptionsList.map((so) => so.uuid));
    updateSheetDetails({
      // biome-ignore lint/style/useNamingConvention: existing error
      payload: {signup_options: newSignupOptionsList},
      appendIfArray: true,
      saveChanges: true,
    });

    dispatch({
      type: UPDATE_MULTIPLE_DATES_MODAL,
      payload: {...initialMultipleDatesModalState, open: false},
    });
  }, [dates, fieldSchema, blueprintType, updateSheetDetails, trackChangeUpstream]);

  // clean up function
  // biome-ignore lint/correctness/useExhaustiveDependencies: existing error
  useEffect(
    () => () =>
      dispatch({
        type: UPDATE_MULTIPLE_DATES_MODAL_REPEATING_TAB_MONTHLY_SPECIFIER,
        payload: {ordinal: '1st', day: moment(startDate).day().toString()},
      }),
    [startDate]
  );

  return (
    <div className="multiple-dates__overlay-content">
      <AddMultipleDates />

      <Divider marginTop="2rem" marginBottom="2rem" />

      <div className="add-multiple-dates__form-actions">
        <Button
          data-qa-id="cancel-button"
          variant="secondary"
          size="large"
          onClick={onClose}
          fullWidth
        >
          Cancel
        </Button>
        <Button
          data-qa-id="add-slots-button"
          onClick={onClickOk}
          size="large"
          fullWidth
          disabled={okButtonDisabled}
        >
          {dates?.length >= 1 ? `Add ${utils.pluralize('day', dates?.length)}` : 'Add days'}
        </Button>
      </div>
    </div>
  );
};
